import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaFileExport, FaSearch, FaUserTie, FaCalendarAlt, FaHandshake } from 'react-icons/fa';
import Swal from 'sweetalert2';

import Wrapper from '../../components/Wrapper';
import axiosInstance from '../../utils/axios';
import { fetchFieldOfficer } from '../../store/features/fieldOfficerSlice';
import { fetchPartners } from '../../store/features/partnersSlice';
import { getRole } from '../../utils/role';
import '../../styles/export.css';

const Export = () => {
  const dispatch = useDispatch();
  const role = getRole();
  
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = role === 'SUPPORT' ? 100 : 99999;

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedFieldOfficer, setSelectedFieldOfficer] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Bulk sample IDs filter
  const [bulkIdsOpen, setBulkIdsOpen] = useState(false);
  const [sampleIdsText, setSampleIdsText] = useState('');
  const [submittedSampleIds, setSubmittedSampleIds] = useState([]);
  
  // Partner searchable dropdown state
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerDropdownOpen, setPartnerDropdownOpen] = useState(false);
  const partnerDropdownRef = useRef(null);

  // Field officer searchable dropdown state
  const [foSearch, setFoSearch] = useState('');
  const [foDropdownOpen, setFoDropdownOpen] = useState(false);
  const foDropdownRef = useRef(null);

  const { fieldOfficers = [] } = useSelector((state) => state.fieldOfficers);
  const partnersList = useSelector((state) => state.partners?.partners ?? []);

  // Local list used by the dropdown — either all FOs (no partner) or partner-scoped FOs
  const [displayedFieldOfficers, setDisplayedFieldOfficers] = useState([]);

  // Fetch field officers and/or partners on mount by role
  useEffect(() => {
    if ((role === 'PARTNER' || role === 'TEAM_MEMBER')) {
      dispatch(fetchFieldOfficer({ role: 'PARTNER' }));
    } else if (role === 'SUPPORT') {
      dispatch(fetchFieldOfficer({ role: 'SUPPORT', page: 1, limit: 500 }));
      dispatch(fetchPartners({ page: 1, limit: 500 }));
    }
  }, [dispatch, role]);

  // Close partner dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(e.target)) {
        setPartnerDropdownOpen(false);
        setPartnerSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close field officer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (foDropdownRef.current && !foDropdownRef.current.contains(e.target)) {
        setFoDropdownOpen(false);
        setFoSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keep displayed FOs in sync with the global list (no partner selected)
  useEffect(() => {
    if (!selectedPartner) {
      setDisplayedFieldOfficers(fieldOfficers);
    }
  }, [fieldOfficers, selectedPartner]);

  // When a partner is selected (SUPPORT role), fetch only that partner's field officers
  useEffect(() => {
    if (role !== 'SUPPORT') return;
    setSelectedFieldOfficer('');
    if (!selectedPartner) {
      setDisplayedFieldOfficers(fieldOfficers);
      return;
    }
    axiosInstance
      .get(`/super-admin/${selectedPartner}/field-officers`)
      .then(({ data }) => {
        setDisplayedFieldOfficers(Array.isArray(data?.fieldOfficers) ? data.fieldOfficers : []);
      })
      .catch(() => setDisplayedFieldOfficers([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartner, role]);

  // Reset to page 1 when filters change (SuperAdmin)
  useEffect(() => {
    if (role === 'SUPPORT') setPage(1);
  }, [role, selectedPartner, selectedFieldOfficer, startDate, endDate, submittedSearch, submittedSampleIds]);

  // Fetch reports from backend (filters applied by API; SuperAdmin uses pagination)
  useEffect(() => {
    const fetchExportReports = async () => {
      setIsLoading(true);
      try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (submittedSearch) params.search = submittedSearch;
        if (submittedSampleIds.length > 0) params.sample_ids = submittedSampleIds.join(',');

        let url = '';
        if ((role === 'PARTNER' || role === 'TEAM_MEMBER')) {
          url = '/admin/export/reports';
          if (selectedFieldOfficer) params.field_officer_id = selectedFieldOfficer;
        } else if (role === 'SUPPORT') {
          url = '/super-admin/export/reports';
          if (selectedPartner) params.partner_id = selectedPartner;
          if (selectedFieldOfficer) params.field_officer_id = selectedFieldOfficer;
          params.page = page;
          params.limit = pageSize;
        } else if (role === 'FIELD_OFFICER') {
          url = '/field-officer/export/reports';
        }
        const { data } = await axiosInstance.get(url, { params });
        if (role === 'SUPPORT' && data && typeof data.totalCount === 'number') {
          setReports(data.reports || []);
          setFilteredReports(data.reports || []);
          setTotalCount(data.totalCount);
        } else {
          const list = Array.isArray(data) ? data : [];
          setReports(list);
          setFilteredReports(list);
          setTotalCount(list.length);
        }
      } catch (error) {
        console.error('Error fetching export reports:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.error || 'Failed to fetch reports. Please try again.',
        });
        setReports([]);
        setFilteredReports([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    if ((role === 'PARTNER' || role === 'TEAM_MEMBER') || role === 'SUPPORT' || role === 'FIELD_OFFICER') {
      fetchExportReports();
    }
  }, [role, selectedPartner, selectedFieldOfficer, startDate, endDate, page, pageSize, submittedSearch, submittedSampleIds]);


  // Helper to color code pH values
  const getPhColor = (ph) => {
    if (!ph || ph === '-') return '';
    const phValue = parseFloat(ph);
    if (isNaN(phValue)) return '';
    if (phValue < 6.0) return 'text-orange-600 font-medium';
    if (phValue > 7.5) return 'text-blue-600 font-medium';
    return 'text-emerald-600 font-medium';
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  };

  const formatNumber = (value, decimals = 1) => {
    if (value === null || value === undefined || value === '' || value === '-') return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toFixed(decimals);
  };

  const reportsToExportRows = (list, startIndex = 0) =>
    list.map((report, i) => ({
      'S.NO.': startIndex + i + 1,
      'FARMER NAME': report.farmer_name || '-',
      'MOBILE NO': report.mobile_no || '-',
      'SAMPLE ID': (report.sample_id || '').toUpperCase(),
      'STATUS': report.report_status === 'R' ? 'under_review' : report.report_status === 'S' ? 'good' : '-',
      'ADDRESS': report.address || '-',
      'FARM NAME': report.farm_name || '-',
      'FARM LOCATION (LAT, LONG)': report.farm_location || '-',
      'PREVIOUS CROP': (report.previous_crop || '').toUpperCase(),
      'NEXT CROP': (report.next_crop || '').toUpperCase(),
      'FIELD OFFICER NAME': report.field_officer_name || '-',
      'DATE': report.created_at
        ? new Date(Date.parse(report.created_at)).toLocaleDateString()
        : '-',
      'pH': report.ph || '-',
      'EC (dS/m)': report.ec || '-',
      'OC (%)': report.oc || '-',
      'NITROGEN (kg/ha)': report.nitrogen || '-',
      'PHOSPHORUS (kg/ha)': report.phosphorus || '-',
      'POTASSIUM (kg/ha)': report.potassium || '-',
      'SULPHUR (mg/kg)': report.sulphur || '-',
      'MAGNESIUM (ppm)': report.magnesium || '-',
      'CALCIUM (ppm)': report.calcium || '-',
      'ZINC (mg/kg)': report.zinc || '-',
      'IRON (mg/kg)': report.iron || '-',
      'COPPER (mg/kg)': report.copper || '-',
      'BORON (mg/kg)': report.boron || '-',
      'C:N RATIO': report.cn_ratio || '-',
      'CEC': report.cec || '-',
      'CA:MG RATIO': report.ca_mg_ratio || '-',
      'K:MG RATIO': report.k_mg_ratio || '-',
    }));

  // Export current page (or all for non-SuperAdmin) to Excel
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      const exportData = reportsToExportRows(filteredReports, role === 'SUPPORT' ? (page - 1) * pageSize : 0);

      // Use existing export utility but with custom data
      const XLSX = require('xlsx');
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Export Data');
      XLSX.writeFile(workbook, `reports_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Exported ${filteredReports.length} records successfully!`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'Failed to export data. Please try again.',
      });
    } finally {
      setIsExporting(false);
    }
  };

  // SuperAdmin: fetch all pages and export to Excel
  const handleExportAllToExcel = async () => {
    if (role !== 'SUPPORT' || totalCount <= 0) return;
    try {
      setIsExporting(true);
      const params = { limit: pageSize };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedPartner) params.partner_id = selectedPartner;
      if (selectedFieldOfficer) params.field_officer_id = selectedFieldOfficer;
      if (submittedSampleIds.length > 0) params.sample_ids = submittedSampleIds.join(',');
      const allReports = [];
      const totalPages = Math.ceil(totalCount / pageSize);
      for (let p = 1; p <= totalPages; p++) {
        const { data } = await axiosInstance.get('/super-admin/export/reports', { params: { ...params, page: p } });
        if (data.reports && data.reports.length) allReports.push(...data.reports);
      }
      const exportData = reportsToExportRows(allReports, 0);
      const XLSX = require('xlsx');
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Export Data');
      XLSX.writeFile(workbook, `reports_export_all_${new Date().toISOString().split('T')[0]}.xlsx`);
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Exported ${allReports.length} records successfully!`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Export all error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: error.response?.data?.error || 'Failed to export all data. Please try again.',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Wrapper>
      <div className="export-page py-4 w-100">
        {role === 'FIELD_OFFICER' && (
          <p className="text-slate-600 text-sm mb-2">Reports for farmers linked to you. Use date range to filter, then export.</p>
        )}
        {role === 'SUPPORT' && (
          <p className="text-slate-600 text-sm mb-2">Complete report data. Filter by partner and/or field officer, then export.</p>
        )}
        {/* Control Panel - fixed at top, no scroll */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-4 flex-shrink-0">
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-center">
            
            {/* Left Side: Search + Bulk IDs toggle */}
            <div className="flex gap-2 w-full max-w-xs sm:max-w-sm items-center">
              <form 
                className="relative flex gap-2 flex-1"
                onSubmit={(e) => { 
                  e.preventDefault(); 
                  setSubmittedSearch(searchQuery.trim()); 
                }}
              >
                <div className="relative w-full">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search farmer or sample ID..."
                    className="pl-10 w-full rounded-md border border-slate-300 py-1.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-colors"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button 
                  type="submit" 
                  className="flex items-center justify-center bg-emerald-100 hover:bg-emerald-200 text-emerald-600 p-2 rounded-md transition-colors"
                >
                  <FaSearch className="h-4 w-4" />
                </button>
              </form>
              {/* Bulk Sample IDs toggle */}
              <button
                type="button"
                title="Filter by multiple sample IDs"
                onClick={() => setBulkIdsOpen((o) => !o)}
                className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors whitespace-nowrap ${
                  submittedSampleIds.length > 0
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : bulkIdsOpen
                    ? 'bg-emerald-50 border-emerald-400 text-emerald-700'
                    : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                }`}
              >
                Bulk IDs
                {submittedSampleIds.length > 0 && (
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-white text-emerald-700 text-xs font-bold">
                    {submittedSampleIds.length}
                  </span>
                )}
              </button>
            </div>

            {/* Right Side: Filters & Actions */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto items-center">
              
              {/* Partner Searchable Dropdown (SuperAdmin only) */}
              {role === 'SUPPORT' && (() => {
                const selectedPartnerLabel = selectedPartner
                  ? (partnersList.find((p) => String(p.admin_id) === String(selectedPartner))
                      ?.entity_name ||
                    partnersList.find((p) => String(p.admin_id) === String(selectedPartner))?.name ||
                    selectedPartner)
                  : 'All Partners';
                const filteredPartners = partnersList.filter((p) => {
                  const label = (p.entity_name || p.name || String(p.admin_id)).toLowerCase();
                  return label.includes(partnerSearch.toLowerCase());
                });
                return (
                  <div className="relative w-full sm:w-auto" ref={partnerDropdownRef}>
                    {/* Trigger button */}
                    <button
                      type="button"
                      className="flex items-center gap-2 pl-9 pr-8 py-2 w-full sm:w-48 rounded-md border border-slate-300 bg-white text-sm text-slate-700 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none cursor-pointer text-left"
                      onClick={() => {
                        setPartnerDropdownOpen((o) => !o);
                        setPartnerSearch('');
                      }}
                    >
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaHandshake className="h-4 w-4 text-slate-500" />
                      </span>
                      <span className="truncate flex-1">{selectedPartnerLabel}</span>
                      <span className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                        <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </button>

                    {/* Dropdown panel */}
                    {partnerDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-64 bg-white border border-slate-200 rounded-md shadow-lg">
                        {/* Search input */}
                        <div className="p-2 border-b border-slate-100">
                          <div className="relative">
                            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                            <input
                              autoFocus
                              type="text"
                              placeholder="Search partner..."
                              className="pl-7 pr-3 py-1.5 w-full rounded border border-slate-300 text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                              value={partnerSearch}
                              onChange={(e) => setPartnerSearch(e.target.value)}
                            />
                          </div>
                        </div>
                        {/* Options list */}
                        <ul className="max-h-52 overflow-y-auto py-1">
                          <li
                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${!selectedPartner ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'}`}
                            onClick={() => {
                              setSelectedPartner('');
                              setPartnerDropdownOpen(false);
                              setPartnerSearch('');
                            }}
                          >
                            All Partners
                          </li>
                          {filteredPartners.length > 0 ? (
                            filteredPartners.map((p) => (
                              <li
                                key={p.admin_id}
                                className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${String(selectedPartner) === String(p.admin_id) ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'}`}
                                onClick={() => {
                                  setSelectedPartner(p.admin_id);
                                  setPartnerDropdownOpen(false);
                                  setPartnerSearch('');
                                }}
                              >
                                {p.entity_name || p.name || p.admin_id}
                              </li>
                            ))
                          ) : (
                            <li className="px-3 py-2 text-sm text-slate-400 italic">No partners found</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Field Officer Searchable Dropdown (Partner & SuperAdmin only) */}
              {((role === 'PARTNER' || role === 'TEAM_MEMBER')|| role === 'SUPPORT') && (() => {
                const selectedFoLabel = selectedFieldOfficer
                  ? (displayedFieldOfficers.find((fo) => String(fo.field_officer_id) === String(selectedFieldOfficer))?.name || selectedFieldOfficer)
                  : 'All Field Officers';
                const filteredFos = displayedFieldOfficers.filter((fo) =>
                  (fo.name || '').toLowerCase().includes(foSearch.toLowerCase())
                );
                return (
                  <div className="relative w-full sm:w-auto" ref={foDropdownRef}>
                    {/* Trigger button */}
                    <button
                      type="button"
                      className="flex items-center gap-2 pl-9 pr-8 py-2 w-full sm:w-48 rounded-md border border-slate-300 bg-white text-sm text-slate-700 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none cursor-pointer text-left"
                      onClick={() => {
                        setFoDropdownOpen((o) => !o);
                        setFoSearch('');
                      }}
                    >
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaUserTie className="h-4 w-4 text-slate-500" />
                      </span>
                      <span className="truncate flex-1">{selectedFoLabel}</span>
                      <span className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                        <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </button>

                    {/* Dropdown panel */}
                    {foDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-64 bg-white border border-slate-200 rounded-md shadow-lg">
                        {/* Search input */}
                        <div className="p-2 border-b border-slate-100">
                          <div className="relative">
                            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                            <input
                              autoFocus
                              type="text"
                              placeholder="Search field officer..."
                              className="pl-7 pr-3 py-1.5 w-full rounded border border-slate-300 text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                              value={foSearch}
                              onChange={(e) => setFoSearch(e.target.value)}
                            />
                          </div>
                        </div>
                        {/* Options list */}
                        <ul className="max-h-52 overflow-y-auto py-1">
                          <li
                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${!selectedFieldOfficer ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'}`}
                            onClick={() => {
                              setSelectedFieldOfficer('');
                              setFoDropdownOpen(false);
                              setFoSearch('');
                            }}
                          >
                            All Field Officers
                          </li>
                          {filteredFos.length > 0 ? (
                            filteredFos.map((fo) => (
                              <li
                                key={fo.field_officer_id}
                                className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${String(selectedFieldOfficer) === String(fo.field_officer_id) ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'}`}
                                onClick={() => {
                                  setSelectedFieldOfficer(fo.field_officer_id);
                                  setFoDropdownOpen(false);
                                  setFoSearch('');
                                }}
                              >
                                {fo.name}
                              </li>
                            ))
                          ) : (
                            <li className="px-3 py-2 text-sm text-slate-400 italic">
                              {displayedFieldOfficers.length === 0
                                ? (selectedPartner ? 'No field officers for this partner' : 'Loading...')
                                : 'No field officers found'}
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Date Range Group */}
              <div className="flex items-center gap-2 bg-slate-50 rounded-md border border-slate-300 p-1 w-full sm:w-auto">
                <div className="flex items-center px-2">
                  <FaCalendarAlt className="h-4 w-4 text-slate-500" />
                </div>
                <input
                  type="date"
                  className="bg-transparent text-sm text-slate-700 outline-none w-32 cursor-pointer border-0"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  title="Start Date"
                />
                <span className="text-slate-400">-</span>
                <input
                  type="date"
                  className="bg-transparent text-sm text-slate-700 outline-none w-32 cursor-pointer border-0"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  title="End Date"
                />
              </div>

              {/* Download Button(s) */}
              <button
                onClick={handleExportToExcel}
                disabled={isExporting || filteredReports.length === 0}
                className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium transition-colors shadow-sm w-full sm:w-auto"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <FaFileExport className="h-4 w-4" />
                    {role === 'SUPPORT' ? `Download page (${filteredReports.length})` : `Download (${filteredReports.length})`}
                  </>
                )}
              </button>
              {role === 'SUPPORT' && totalCount > pageSize && (
                <button
                  type="button"
                  onClick={handleExportAllToExcel}
                  disabled={isExporting || totalCount === 0}
                  className="flex items-center justify-center gap-2 bg-slate-600 hover:bg-slate-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium transition-colors shadow-sm w-full sm:w-auto"
                >
                  Export ({totalCount})
                </button>
              )}

            </div>
          </div>

          {/* Bulk Sample IDs panel — second row, collapsible */}
          {bulkIdsOpen && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs font-medium text-slate-500 mb-2">
                Enter one Sample ID per line or separate with commas — only matching records will be shown.
              </p>
              <textarea
                rows={3}
                placeholder={"e.g.\nABC123\nDEF456, GHI789"}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none resize-none"
                value={sampleIdsText}
                onChange={(e) => setSampleIdsText(e.target.value)}
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    const ids = sampleIdsText
                      .split(/[\n,]/)
                      .map((s) => s.trim())
                      .filter(Boolean);
                    setSubmittedSampleIds(ids);
                  }}
                  className="px-4 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors shadow-sm"
                >
                  Apply Filter
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSampleIdsText('');
                    setSubmittedSampleIds([]);
                  }}
                  className="px-4 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-600 text-xs font-medium transition-colors"
                >
                  Clear
                </button>
                {submittedSampleIds.length > 0 && (
                  <span className="ml-1 text-xs text-emerald-700 font-medium">
                    ✓ Filtering by {submittedSampleIds.length} sample ID{submittedSampleIds.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Table Container - only this area scrolls */}
        <div className="export-page__scroll">
        {isLoading ? (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            <p className="mt-3 text-slate-500">Loading reports data...</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center text-slate-500">
            No records found matching your filters.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden flex flex-col">
            <div className="overflow-auto custom-scrollbar">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    {/* Sticky Columns for identification */}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200 sticky left-0 z-10 border-r border-slate-200 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)]">
                      Farmer Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Mobile No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Sample ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Address
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Farm Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Farm Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Prev Crop
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Next Crop
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Field Officer
                    </th>
                    
                    {/* Chemical Properties */}
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-blue-50/50 border-b border-slate-200">
                      pH
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-blue-50/50 border-b border-slate-200">
                      EC
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-blue-50/50 border-b border-slate-200">
                      OC %
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Nitrogen (N)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Phosphorus (P)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Potassium (K)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Sulphur (S)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Magnesium (Mg)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Calcium (Ca)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Zinc (Zn)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Iron (Fe)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Copper (Cu)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50 border-b border-slate-200">
                      Boron (B)
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-blue-50/50 border-b border-slate-200">
                      C:N Ratio
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-blue-50/50 border-b border-slate-200">
                      CEC
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-blue-50/50 border-b border-slate-200">
                      Ca:Mg Ratio
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-blue-50/50 border-b border-slate-200">
                      K:Mg Ratio
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {filteredReports.map((report, idx) => (
                    <tr key={report.report_id || idx} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-sm border-b border-slate-100 sticky left-0 bg-white hover:bg-slate-50 z-10 font-medium text-slate-900 border-r border-slate-200 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)] max-w-[180px] truncate">
                        {formatValue(report.farmer_name)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100">
                        {formatValue(report.mobile_no)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100">
                        <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded text-slate-600">
                          {formatValue(report.sample_id).toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap border-b border-slate-100">
                        {report.report_status === 'R' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                            under_review
                          </span>
                        ) : report.report_status === 'S' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">
                            good
                          </span>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 border-b border-slate-100 max-w-xs truncate" title={formatValue(report.address)}>
                        {formatValue(report.address)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 max-w-[150px] truncate" title={formatValue(report.farm_name)}>
                        {formatValue(report.farm_name)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 font-mono text-xs">
                        {formatValue(report.farm_location)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100">
                        {formatValue(report.previous_crop)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100">
                        {formatValue(report.next_crop)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100">
                        {formatValue(report.field_officer_name)}
                      </td>
                      
                      {/* Chem Data - Center aligned numbers */}
                      <td className={`px-4 py-3 text-sm whitespace-nowrap border-b border-slate-100 text-center ${getPhColor(report.ph)}`}>
                        {formatNumber(report.ph, 2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-center">
                        {formatNumber(report.ec, 3)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-center">
                        {formatNumber(report.oc, 3)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-right font-medium text-emerald-700 bg-emerald-50/30">
                        {formatValue(report.nitrogen)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-right">
                        {formatValue(report.phosphorus)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-right">
                        {formatValue(report.potassium)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-right">
                        {formatValue(report.sulphur)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-right">
                        {formatValue(report.magnesium)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-right">
                        {formatValue(report.calcium)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-right">
                        {formatValue(report.zinc)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-right">
                        {formatValue(report.iron)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-right">
                        {formatValue(report.copper)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-right">
                        {formatValue(report.boron)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-center">
                        {formatValue(report.cn_ratio)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-center">
                        {formatValue(report.cec)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-center">
                        {formatValue(report.ca_mg_ratio)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 text-center">
                        {formatValue(report.k_mg_ratio)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-slate-50 px-4 py-3 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span>
                  {role === 'SUPPORT' && totalCount > 0
                    ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount}`
                    : `Showing ${filteredReports.length} entries`}
                </span>
                {role === 'SUPPORT' && totalCount > pageSize && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="px-2 py-1 rounded border border-slate-300 bg-white text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 text-xs font-medium"
                    >
                      Previous
                    </button>
                    <span className="px-2 text-slate-600">
                      Page {page} of {Math.ceil(totalCount / pageSize) || 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page * pageSize >= totalCount}
                      className="px-2 py-1 rounded border border-slate-300 bg-white text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 text-xs font-medium"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
              <span>Soil Testing Lab Report</span>
            </div>
          </div>
        )}
        </div>
      </div>
    </Wrapper>
  );
};

export default Export;
